import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface OAModalData {
  visible: boolean;
  emailDetail?: any;
  visibleRouteKeys?: string[];
}

type OAModalType = 'process' | 'list' | 'saasProcess' | 'saasList' | 'careProcess' | 'careList';

interface OAModalsState {
  [key: string]: OAModalData;
}

interface OAProcessModalContextProps {
  modalsState: OAModalsState;
  setModalData: (modalType: OAModalType, data: Partial<OAModalData>) => void;
  openModal: (modalType: OAModalType, visibleRouteKeys?: string[]) => void;
  closeModal: (modalType: OAModalType) => void;
  updateAllModalsEmailDetail: (emailDetail: any, type: 'email' | 'saas' | 'care') => void;
  clearModalsEmailDetail: (type: 'email' | 'saas' | 'care', id?: string | number) => void;
}

const OAProcessModalContext = createContext<OAProcessModalContextProps>({
  modalsState: {},
  setModalData: () => { },
  openModal: () => { },
  closeModal: () => { },
  updateAllModalsEmailDetail: () => { },
  clearModalsEmailDetail: () => { },
});

export const useOAProcessModalContext = () => {
  const context = useContext(OAProcessModalContext);
  if (!context) {
    throw new Error('useOAProcessModalContext must be used within OAProcessModalProvider');
  }
  return context;
};

interface OAProcessModalProviderProps {
  children: ReactNode;
}

export const OAProcessModalProvider: React.FC<OAProcessModalProviderProps> = ({ children }) => {
  const [modalsState, setModalsState] = useState<OAModalsState>({
    process: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    list: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    saasProcess: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    saasList: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    careProcess: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    careList: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
  });

  const setModalData = useCallback((modalType: OAModalType, data: Partial<OAModalData>) => {
    setModalsState((prev) => ({
      ...prev,
      [modalType]: {
        ...prev[modalType],
        ...data,
      },
    }));
  }, []);

  const openModal = useCallback((modalType: OAModalType, visibleRouteKeys?: string[]) => {
    setModalsState((prev) => {
      return {
        ...prev,
        [modalType]: {
          visible: true,
          emailDetail: prev[modalType]?.emailDetail,
          visibleRouteKeys,
        },
      };
    });
  }, []);

  const closeModal = useCallback((modalType: OAModalType) => {
    setModalsState((prev) => ({
      ...prev,
      [modalType]: {
        ...prev[modalType],
        visible: false,
      },
    }));
  }, []);

  // 更新所有 modal 的 emailDetail（用于邮件详情变化时同步）
  const updateAllModalsEmailDetail = useCallback((emailDetail: any, type: 'email' | 'saas' | 'care') => {
    // 邮件工单 - 更新'process' | 'list'
    // SAAS工单 - 更新'saasProcess' | 'saasList'
    // Care工单 - 更新'careProcess' | 'careList'
    let modalTypes: OAModalType[];
    switch (type) {
      case 'email':
        modalTypes = ['process', 'list'];
        break;
      case 'saas':
        modalTypes = ['saasProcess', 'saasList'];
        break;
      case 'care':
        modalTypes = ['careProcess', 'careList'];
        break;
      default:
        modalTypes = [];
        break;
    }
    modalTypes.forEach((modalType) => {
      setModalsState((prev) => ({
        ...prev,
        [modalType]: {
          ...prev[modalType],
          emailDetail,
        },
      }));
    });
  }, []);

  // 按类型清空对应 modal 槽位的 emailDetail（用于详情卸载时释放大对象引用）。
  // 必须传入 id：槽位里的 emailDetail 与 id 不一致（已被新工单覆盖）或本实例从未同步时跳过，
  // 防止「打开新详情 → 旧详情卸载」时序误清新实例数据（keepalive 下多实例可能共存）。
  const clearModalsEmailDetail = useCallback((type: 'email' | 'saas' | 'care', id?: string | number) => {
    if (id === undefined) return;
    let modalTypes: OAModalType[];
    switch (type) {
      case 'email':
        modalTypes = ['process', 'list'];
        break;
      case 'saas':
        modalTypes = ['saasProcess', 'saasList'];
        break;
      case 'care':
        modalTypes = ['careProcess', 'careList'];
        break;
      default:
        modalTypes = [];
        break;
    }
    modalTypes.forEach((modalType) => {
      setModalsState((prev) => {
        if (prev[modalType]?.emailDetail?.id !== id) {
          return prev;
        }
        return {
          ...prev,
          [modalType]: {
            ...prev[modalType],
            emailDetail: undefined,
          },
        };
      });
    });
  }, []);

  return (
    <OAProcessModalContext.Provider
      value={{
        modalsState,
        setModalData,
        openModal,
        closeModal,
        updateAllModalsEmailDetail,
        clearModalsEmailDetail,
      }}
    >
      {children}
    </OAProcessModalContext.Provider>
  );
};

